import { useEffect, useMemo, useState } from "react";
import { getLocalServerURL, saveBranchObjectsURL } from "../../../libraries/apiUrl";
import { copyToClipboard, showToast } from "../../../libraries/clipboardUtils";
import { _delete, _postForm, _putForm } from "../../../libraries/fetch";

function firstPresent(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return "";
}

function gymGalleryFileUrl(branchId, fileName) {
  const text = String(fileName || "").trim();
  if (!text) return "";
  if (
    text.startsWith("data:") ||
    text.startsWith("http://") ||
    text.startsWith("https://") ||
    text.startsWith("blob:")
  ) {
    return text;
  }
  const id = encodeURIComponent(String(branchId || "").trim());
  const file = encodeURIComponent(text);
  return `${getLocalServerURL()}/assets/layout/images/companies/branch-${id}/Gym%20Gallary/${file}`;
}

function resolveMediaUrl(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (
    text.startsWith("data:") ||
    text.startsWith("http://") ||
    text.startsWith("https://") ||
    text.startsWith("blob:")
  ) {
    return text;
  }
  const base = getLocalServerURL();
  if (text.startsWith("/")) {
    return `${base}${text}`;
  }
  return `${base}/assets/layout/images/companies/${encodeURI(text).replace(/#/g, "%23")}`;
}

function collectImageUrls(value, bucket) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectImageUrls(item, bucket));
    return;
  }
  if (typeof value === "object") {
    collectImageUrls(
      value.url || value.src || value.path || value.image || value.fileName || value.filename,
      bucket
    );
    return;
  }
  const url = resolveMediaUrl(value);
  if (url && !bucket.includes(url)) {
    bucket.push(url);
  }
}

function formatCoordinate(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return String(Math.round(numeric * 1000000) / 1000000);
  }
  return String(value).trim();
}

function hasCoordinate(value) {
  return value === 0 || (value !== null && value !== undefined && String(value).trim() !== "");
}

function readCoordinatePair(branch) {
  const location = branch.location && typeof branch.location === "object" ? branch.location : null;
  const coordinates = branch.coordinates;
  const savedPair = Array.isArray(coordinates) && coordinates[0] && !Array.isArray(coordinates[0])
    ? coordinates[0]
    : null;
  let latitude = firstPresent(
    savedPair?.latitude,
    branch.latitude,
    branch.lat,
    location?.latitude,
    location?.lat
  );
  let longitude = firstPresent(
    savedPair?.longitude,
    branch.longitude,
    branch.lng,
    branch.long,
    location?.longitude,
    location?.lng
  );

  const nestedPair = coordinates && typeof coordinates === "object" && Array.isArray(coordinates.coordinates)
    ? coordinates.coordinates
    : null;
  const pair = location && Array.isArray(location.coordinates) && location.coordinates.length >= 2
    ? location.coordinates
    : Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates
      : nestedPair && nestedPair.length >= 2
        ? nestedPair
        : null;

  if (pair) {
    if (!hasCoordinate(longitude)) longitude = pair[0];
    if (!hasCoordinate(latitude)) latitude = pair[1];
  } else if (coordinates && typeof coordinates === "object") {
    if (!hasCoordinate(latitude)) latitude = firstPresent(coordinates.latitude, coordinates.lat);
    if (!hasCoordinate(longitude)) longitude = firstPresent(coordinates.longitude, coordinates.lng, coordinates.long);
  }

  return {
    latitude: formatCoordinate(latitude),
    longitude: formatCoordinate(longitude)
  };
}

function formatClock(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
  }
  const text = String(value).trim();
  const clock = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (clock) {
    return `${clock[1].padStart(2, "0")}:${clock[2]}`;
  }
  if (text.includes("T")) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
  }
  return text;
}

function readSchedule(branch) {
  const source = firstPresent(
    branch.timings,
    branch.timing,
    branch.workingHours,
    branch.schedule,
    branch.openingHours
  );
  if (!Array.isArray(source)) return [];
  return source
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const day = firstPresent(item.day, item.name, item.label);
      const start = formatClock(firstPresent(item.starttime, item.startTime, item.start, item.open, item.from));
      const end = formatClock(firstPresent(item.endtime, item.endTime, item.end, item.close, item.to));
      if (!day && !start && !end) return null;
      return {
        day: day || "Day",
        start,
        end,
        enabled: item.enabled !== false
      };
    })
    .filter(Boolean);
}

function readPassportRating(branch) {
  const raw = firstPresent(
    branch.passportRate,
    branch.passportRating,
    branch.passport_rating,
    branch.passport?.rating,
    branch.publicPagesDate?.passportRating,
    branch.rating
  );
  if (raw && typeof raw === "object") {
    return firstPresent(raw.value, raw.rating, raw.score, raw.label);
  }
  return raw;
}

function readGallery(branch) {
  const bucket = [];
  const branchId = branch._id || branch.id;
  if (Array.isArray(branch.gymGallery)) {
    branch.gymGallery.forEach((item) => {
      const name = typeof item === "string"
        ? item
        : item?.fileName || item?.filename || item?.url || item?.image;
      const url = gymGalleryFileUrl(branchId, name);
      if (url && !bucket.includes(url)) bucket.push(url);
    });
  }
  [
    branch.gallery,
    branch.gymGallery,
    branch.images,
    branch.photos,
    branch.branchImages,
    branch.publicPagesDate?.gallery,
    branch.publicPagesDate?.images,
    branch.publicPagesDate?.photos
  ].forEach((value) => collectImageUrls(value, bucket));
  return bucket;
}

function buildProfile(branch) {
  const record = branch && typeof branch === "object" ? branch : {};
  const coordinates = readCoordinatePair(record);
  return {
    images: readGallery(record),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    rating: readPassportRating(record),
    startTime: formatClock(firstPresent(record.starttime, record.startTime, record.openingTime, record.openTime)),
    endTime: formatClock(firstPresent(record.endtime, record.endTime, record.closingTime, record.closeTime)),
    schedule: readSchedule(record)
  };
}

const cardStyle = {
  backgroundColor: "#2a2d35",
  border: "1px solid #3a3f4a",
  borderRadius: "12px",
  padding: "16px",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  position: "relative",
  boxShadow: "none",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box"
};

const titleStyle = {
  fontSize: "16px",
  fontWeight: "700",
  color: "#fff",
  margin: 0,
  borderBottom: "2px solid #187718",
  paddingBottom: "10px"
};

const labelStyle = {
  fontSize: "13px",
  color: "#888",
  marginBottom: "8px"
};

const valueStyle = {
  fontSize: "16px",
  color: "#fff",
  fontWeight: "600",
  wordBreak: "break-word",
  lineHeight: 1.5
};

function hoverCard(element, active) {
  element.style.borderColor = active ? "#187718" : "#3a3f4a";
  element.style.boxShadow = active ? "0 0 0 1px rgba(24, 119, 24, 0.35)" : "none";
}

function ProfileCard({ title, onClick, copiedLabel, children }) {
  return (
    <div
      className="user-card"
      onClick={onClick}
      style={{ ...cardStyle, cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={(event) => hoverCard(event.currentTarget, true)}
      onMouseLeave={(event) => hoverCard(event.currentTarget, false)}
    >
      <div style={{ marginBottom: "14px" }}>
        <h3 style={titleStyle}>{title}</h3>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {children}
      </div>
      {copiedLabel && (
        <div style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
          color: "#fff",
          padding: "10px 16px",
          borderRadius: "8px",
          fontSize: "13px",
          fontWeight: "600",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)"
        }}>
          {copiedLabel}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value || "Not available"}</div>
    </div>
  );
}

function PassportRatingValue({ rating }) {
  if (rating === "" || rating === null || rating === undefined) {
    return <div style={valueStyle}>Not available</div>;
  }

  const numeric = Number(rating);
  const showStars = Number.isFinite(numeric) && numeric >= 0 && numeric <= 5;
  const filled = showStars ? Math.round(numeric) : 0;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <div style={{ fontSize: "32px", fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>{String(rating)}</div>
      {showStars && (
        <div
          style={{ color: "#f5c518", fontSize: "22px", letterSpacing: "3px" }}
          aria-label={`${filled} out of 5`}
        >
          {"★".repeat(filled)}{"☆".repeat(5 - filled)}
        </div>
      )}
    </div>
  );
}

const BRANCH_OBJECT_HEADINGS = [
  "Gym Gallery",
  "Co-ordinates",
  "Passport Rating",
  "Timing"
];

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const popupInputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #4b5563",
  background: "#111827",
  color: "#fff",
  fontSize: 14,
  boxSizing: "border-box"
};

function emptyWeekdayTimes() {
  return WEEKDAYS.reduce((times, day) => {
    times[day] = { selected: false, enabled: true, from: "07:00", to: "19:00" };
    return times;
  }, {});
}

function validateLatitude(value) {
  const text = String(value ?? "").trim();
  if (!text) return "Enter latitude.";
  if (!/^-?\d+(\.\d+)?$/.test(text)) return "Latitude must be a number.";
  const number = Number(text);
  if (number < -90 || number > 90) return "Latitude must be between -90 and 90.";
  return "";
}

function validateLongitude(value) {
  const text = String(value ?? "").trim();
  if (!text) return "Enter longitude.";
  if (!/^-?\d+(\.\d+)?$/.test(text)) return "Longitude must be a number.";
  const number = Number(text);
  if (number < -180 || number > 180) return "Longitude must be between -180 and 180.";
  return "";
}

function validateRating(value) {
  const text = String(value ?? "").trim();
  if (!text) return "Enter a rating number.";
  if (!/^\d+(\.\d+)?$/.test(text)) return "Rating must be a number.";
  return "";
}

function formatDayTiming(item) {
  if (item.enabled === false) return "Off";
  const range = [item.start, item.end].filter(Boolean).join(" - ");
  return range ? `On, ${range}` : "On";
}

function hasPassportRate(value) {
  if (value === 0) return true;
  if (typeof value === "number") return Number.isFinite(value) && value >= 0;
  if (typeof value === "string" && /^\d+(\.\d+)?$/.test(value.trim())) return true;
  return false;
}

function toTimeInput(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  const hours = Number(match[1]);
  if (hours > 23) return "";
  return `${String(hours).padStart(2, "0")}:${match[2]}`;
}

function weekdayTimesFromSchedule(schedule) {
  const times = emptyWeekdayTimes();
  (schedule || []).forEach((item) => {
    if (!item?.day || !times[item.day]) return;
    times[item.day] = {
      selected: true,
      enabled: item.enabled !== false,
      from: toTimeInput(item.start || item.from) || "07:00",
      to: toTimeInput(item.end || item.to) || "19:00"
    };
  });
  return times;
}

function dedicatedBranchObjects(branch) {
  const record = branch && typeof branch === "object" ? branch : {};
  const branchId = record._id || record.id;
  const images = Array.isArray(record.gymGallery)
    ? record.gymGallery
        .map((item) => {
          const name = typeof item === "string"
            ? item
            : item?.fileName || item?.filename || "";
          return gymGalleryFileUrl(branchId, name);
        })
        .filter(Boolean)
    : [];
  const pair = Array.isArray(record.coordinates) &&
    record.coordinates[0] &&
    typeof record.coordinates[0] === "object" &&
    !Array.isArray(record.coordinates[0])
    ? record.coordinates[0]
    : null;
  const latitude = pair && hasCoordinate(pair.latitude) ? formatCoordinate(pair.latitude) : "";
  const longitude = pair && hasCoordinate(pair.longitude) ? formatCoordinate(pair.longitude) : "";
  const rating = hasPassportRate(record.passportRate) ? record.passportRate : "";
  const schedule = Array.isArray(record.timings) ? readSchedule({ timings: record.timings }) : [];
  return { images, latitude, longitude, rating, schedule };
}

function savedField(localObjects, key, fallback) {
  if (localObjects?.cleared) {
    return key === "images" || key === "schedule" ? [] : "";
  }
  if (localObjects && Object.prototype.hasOwnProperty.call(localObjects, key)) {
    return localObjects[key];
  }
  return fallback;
}

const headerButtonStyle = {
  padding: "12px 24px",
  fontSize: "16px",
  fontWeight: "600",
  borderRadius: "8px",
  border: "2px solid #187718",
  backgroundColor: "#187718",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

export default function BranchProfileCards({ branch }) {
  const profile = useMemo(() => buildProfile(branch), [branch]);
  const [copiedCard, setCopiedCard] = useState("");
  const [addPopupOpen, setAddPopupOpen] = useState(false);
  const [selectedHeadings, setSelectedHeadings] = useState([]);
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [latitudeInput, setLatitudeInput] = useState("");
  const [longitudeInput, setLongitudeInput] = useState("");
  const [passportRatingInput, setPassportRatingInput] = useState("");
  const [weekdayTimes, setWeekdayTimes] = useState(emptyWeekdayTimes);
  const [addedObjects, setAddedObjects] = useState(null);
  const [editorMode, setEditorMode] = useState("add");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [galleryAllOpen, setGalleryAllOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const branchId = branch?._id || branch?.id;

  useEffect(() => {
    setAddedObjects(null);
    setEditorMode("add");
    setConfirmDeleteOpen(false);
    setAddPopupOpen(false);
    setConfirmAddOpen(false);
  }, [branchId]);

  function resetAddDraft() {
    setGalleryFiles((current) => {
      current.forEach((file) => URL.revokeObjectURL(file.url));
      return [];
    });
    setLatitudeInput("");
    setLongitudeInput("");
    setPassportRatingInput("");
    setWeekdayTimes(emptyWeekdayTimes());
    setFieldErrors({});
  }

  function openAddPopup() {
    setEditorMode("add");
    setImagePreview(null);
    setGalleryAllOpen(false);
    setConfirmDeleteOpen(false);
    resetAddDraft();
    setSelectedHeadings([]);
    setConfirmAddOpen(false);
    setAddPopupOpen(true);
  }

  function openUpdatePopup() {
    const saved = {
      images: savedField(addedObjects, "images", dedicated.images),
      latitude: savedField(addedObjects, "latitude", dedicated.latitude),
      longitude: savedField(addedObjects, "longitude", dedicated.longitude),
      rating: savedField(addedObjects, "rating", dedicated.rating),
      schedule: savedField(addedObjects, "schedule", dedicated.schedule)
    };
    const headings = [];
    if (saved.images.length) headings.push("Gym Gallery");
    if (saved.latitude || saved.longitude) headings.push("Co-ordinates");
    if (hasPassportRate(saved.rating)) headings.push("Passport Rating");
    if (saved.schedule.length) headings.push("Timing");
    setEditorMode("update");
    setImagePreview(null);
    setGalleryAllOpen(false);
    setConfirmDeleteOpen(false);
    resetAddDraft();
    setSelectedHeadings(headings);
    setLatitudeInput(saved.latitude ? String(saved.latitude) : "");
    setLongitudeInput(saved.longitude ? String(saved.longitude) : "");
    setPassportRatingInput(hasPassportRate(saved.rating) ? String(saved.rating) : "");
    setWeekdayTimes(weekdayTimesFromSchedule(saved.schedule));
    setConfirmAddOpen(false);
    setAddPopupOpen(true);
  }

  function openImagePreview(urls, index) {
    if (!urls.length) return;
    setImagePreview({ urls, index });
  }

  function closeImagePreview() {
    setImagePreview(null);
  }

  function showPreviousImage() {
    setImagePreview((current) => {
      if (!current || current.index <= 0) return current;
      return { ...current, index: current.index - 1 };
    });
  }

  function showNextImage() {
    setImagePreview((current) => {
      if (!current || current.index >= current.urls.length - 1) return current;
      return { ...current, index: current.index + 1 };
    });
  }

  useEffect(() => {
    if (!imagePreview) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape") {
        setImagePreview(null);
        return;
      }
      if (event.key === "ArrowLeft") {
        setImagePreview((current) => {
          if (!current || current.index <= 0) return current;
          return { ...current, index: current.index - 1 };
        });
      }
      if (event.key === "ArrowRight") {
        setImagePreview((current) => {
          if (!current || current.index >= current.urls.length - 1) return current;
          return { ...current, index: current.index + 1 };
        });
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [imagePreview]);

  useEffect(() => {
    if (!galleryAllOpen || imagePreview) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape") setGalleryAllOpen(false);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [galleryAllOpen, imagePreview]);

  function handleGalleryUpload(event) {
    const selectedFiles = Array.from(event.target.files || []);
    const files = selectedFiles.filter((file) => (
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      /\.(png|jpe?g)$/i.test(file.name)
    ));
    if (selectedFiles.length && files.length !== selectedFiles.length) {
      showToast("Only PNG and JPG images are allowed.", "error");
    }
    const uploaded = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${file.size}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      url: URL.createObjectURL(file),
      file
    }));
    if (uploaded.length) {
      setGalleryFiles((current) => [...current, ...uploaded]);
    }
    event.target.value = "";
  }

  function updateWeekdayTime(day, field, value) {
    setWeekdayTimes((current) => ({
      ...current,
      [day]: {
        ...current[day],
        [field]: value
      }
    }));
  }

  function toggleHeading(heading) {
    setSelectedHeadings((current) => (
      current.includes(heading)
        ? current.filter((item) => item !== heading)
        : [...current, heading]
    ));
  }

  function submitAdd() {
    const nextErrors = {};
    if (selectedHeadings.includes("Co-ordinates")) {
      const latitudeError = validateLatitude(latitudeInput);
      const longitudeError = validateLongitude(longitudeInput);
      if (latitudeError) nextErrors.latitude = latitudeError;
      if (longitudeError) nextErrors.longitude = longitudeError;
    }
    if (selectedHeadings.includes("Passport Rating")) {
      const ratingError = validateRating(passportRatingInput);
      if (ratingError) nextErrors.rating = ratingError;
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setConfirmAddOpen(true);
  }

  async function confirmAdd() {
    const branchId = branch?._id || branch?.id;
    if (!branchId || isSaving) {
      if (!branchId) showToast("Branch is not loaded yet.", "error");
      return;
    }

    const formData = new FormData();
    if (selectedHeadings.includes("Gym Gallery")) {
      galleryFiles.forEach((item) => {
        if (item.file) formData.append("gymGallery", item.file, item.file.name);
      });
    }
    if (selectedHeadings.includes("Co-ordinates")) {
      formData.append("coordinates", JSON.stringify([{
        latitude: Number(latitudeInput),
        longitude: Number(longitudeInput)
      }]));
    }
    if (selectedHeadings.includes("Passport Rating")) {
      formData.append("passportRate", String(passportRatingInput).trim());
    }
    if (selectedHeadings.includes("Timing")) {
      formData.append("timings", JSON.stringify(
        WEEKDAYS
          .filter((day) => weekdayTimes[day].selected)
          .map((day) => ({
            day,
            enabled: weekdayTimes[day].enabled,
            from: weekdayTimes[day].from,
            to: weekdayTimes[day].to
          }))
      ));
    }

    setIsSaving(true);
    try {
      const request = editorMode === "update" ? _putForm : _postForm;
      const response = await request(saveBranchObjectsURL(branchId), formData);
      if (!response || response.success === false || response.status === false) {
        showToast(response?.message || response?.error || "Could not save branch objects", "error");
        setConfirmAddOpen(false);
        return;
      }

      const saved = response.branch && typeof response.branch === "object" ? response.branch : response;
      setAddedObjects((current) => {
        const next = { ...(current && !current.cleared ? current : {}), present: true, cleared: false };
        if (selectedHeadings.includes("Gym Gallery")) {
          const names = Array.isArray(saved.gymGallery) ? saved.gymGallery : [];
          next.images = names.length
            ? names
                .map((name) => gymGalleryFileUrl(saved._id || branchId, name))
                .filter(Boolean)
            : (current && !current.cleared && Array.isArray(current.images) ? current.images : []);
        }
        if (selectedHeadings.includes("Co-ordinates")) {
          const pair = Array.isArray(saved.coordinates) ? saved.coordinates[0] : null;
          next.latitude = pair && hasCoordinate(pair.latitude) ? pair.latitude : Number(latitudeInput);
          next.longitude = pair && hasCoordinate(pair.longitude) ? pair.longitude : Number(longitudeInput);
        }
        if (selectedHeadings.includes("Passport Rating")) {
          next.rating = saved.passportRate !== undefined && saved.passportRate !== null
            ? saved.passportRate
            : Number(passportRatingInput);
        }
        if (selectedHeadings.includes("Timing")) {
          const timings = Array.isArray(saved.timings) ? saved.timings : WEEKDAYS
            .filter((day) => weekdayTimes[day].selected)
            .map((day) => ({
              day,
              enabled: weekdayTimes[day].enabled,
              from: weekdayTimes[day].from,
              to: weekdayTimes[day].to
            }));
          next.schedule = timings.map((item) => ({
            day: item.day,
            start: item.from,
            end: item.to,
            enabled: item.enabled !== false
          }));
        }
        return next;
      });

      galleryFiles.forEach((item) => URL.revokeObjectURL(item.url));
      setGalleryFiles([]);
      setImagePreview(null);
      setConfirmAddOpen(false);
      setAddPopupOpen(false);
      setSelectedHeadings([]);
      showToast(editorMode === "update" ? "Branch objects updated" : "Branch objects saved", "success");
    } catch (error) {
      showToast("Could not save branch objects", "error");
      setConfirmAddOpen(false);
    } finally {
      setIsSaving(false);
    }
  }

  async function confirmDelete() {
    if (!branchId || isSaving) {
      if (!branchId) showToast("Branch is not loaded yet.", "error");
      return;
    }
    setIsSaving(true);
    try {
      const response = await _delete(saveBranchObjectsURL(branchId));
      if (!response || response.success === false || response.status === false) {
        showToast(response?.message || response?.error || "Could not delete branch objects", "error");
        setConfirmDeleteOpen(false);
        return;
      }
      setAddedObjects({
        cleared: true,
        present: false,
        images: [],
        latitude: "",
        longitude: "",
        rating: "",
        schedule: []
      });
      setImagePreview(null);
      setGalleryAllOpen(false);
      setConfirmDeleteOpen(false);
      setAddPopupOpen(false);
      showToast("Branch objects deleted", "success");
    } catch (error) {
      showToast("Could not delete branch objects", "error");
      setConfirmDeleteOpen(false);
    } finally {
      setIsSaving(false);
    }
  }

  async function copyCard(text, cardName) {
    const result = await copyToClipboard(text, { label: cardName });
    if (result.success) {
      showToast("Copied!", "success");
      setCopiedCard(cardName);
      window.setTimeout(() => {
        setCopiedCard((current) => (current === cardName ? "" : current));
      }, 2000);
      return;
    }
    showToast(result.message, "error");
  }

  const dedicated = useMemo(() => dedicatedBranchObjects(branch), [branch]);
  const savedView = {
    images: savedField(addedObjects, "images", dedicated.images),
    latitude: savedField(addedObjects, "latitude", dedicated.latitude),
    longitude: savedField(addedObjects, "longitude", dedicated.longitude),
    rating: savedField(addedObjects, "rating", dedicated.rating),
    schedule: savedField(addedObjects, "schedule", dedicated.schedule)
  };
  const hasObjects = Boolean(
    savedView.images.length ||
    savedView.latitude ||
    savedView.longitude ||
    hasPassportRate(savedView.rating) ||
    savedView.schedule.length
  );
  const shownImages = addedObjects?.cleared
    ? []
    : addedObjects && Object.prototype.hasOwnProperty.call(addedObjects, "images")
      ? addedObjects.images
      : profile.images;
  const shownLatitude = addedObjects?.cleared
    ? ""
    : addedObjects && Object.prototype.hasOwnProperty.call(addedObjects, "latitude")
      ? addedObjects.latitude
      : profile.latitude;
  const shownLongitude = addedObjects?.cleared
    ? ""
    : addedObjects && Object.prototype.hasOwnProperty.call(addedObjects, "longitude")
      ? addedObjects.longitude
      : profile.longitude;
  const shownRating = addedObjects?.cleared
    ? ""
    : addedObjects && Object.prototype.hasOwnProperty.call(addedObjects, "rating")
      ? addedObjects.rating
      : profile.rating;
  const shownSchedule = addedObjects?.cleared
    ? []
    : addedObjects && Object.prototype.hasOwnProperty.call(addedObjects, "schedule")
      ? addedObjects.schedule
      : profile.schedule;

  const coordinateText = `Latitude: ${shownLatitude || "Not available"}
Longitude: ${shownLongitude || "Not available"}`;

  const timingText = shownSchedule.length
    ? shownSchedule
        .map((item) => `${item.day}: ${item.start || "Not available"} - ${item.end || "Not available"}`)
        .join("\n")
    : `Start Time: ${profile.startTime || "Not available"}
End Time: ${profile.endTime || "Not available"}`;

  return (
    <div
      style={{
        flex: "1 1 100%",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#2a2d35",
        border: "1px solid #444",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
          flexWrap: "wrap"
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "22px",
            fontWeight: "bold",
            color: "#fff"
          }}
        >
          Branch Objects
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {hasObjects ? (
            <>
              <button
                type="button"
                onClick={openUpdatePopup}
                style={headerButtonStyle}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = "#20a020";
                  event.currentTarget.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "#187718";
                  event.currentTarget.style.transform = "scale(1)";
                }}
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddPopupOpen(false);
                  setConfirmAddOpen(false);
                  setImagePreview(null);
                  setConfirmDeleteOpen(true);
                }}
                style={{
                  ...headerButtonStyle,
                  border: "2px solid #b91c1c",
                  backgroundColor: "#b91c1c",
                  boxShadow: "0 4px 12px rgba(185, 28, 28, 0.3)"
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = "#dc2626";
                  event.currentTarget.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "#b91c1c";
                  event.currentTarget.style.transform = "scale(1)";
                }}
              >
                Delete
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={openAddPopup}
              style={headerButtonStyle}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = "#20a020";
                event.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = "#187718";
                event.currentTarget.style.transform = "scale(1)";
              }}
            >
              Add
            </button>
          )}
        </div>
      </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
        gap: "16px",
        alignItems: "stretch"
      }}
    >
      <ProfileCard title="Gym Gallery">
        {shownImages.length ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(shownImages.length, 3)}, minmax(0, 1fr))`,
                gap: "8px"
              }}
            >
              {shownImages.slice(0, 3).map((url, index) => (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  onClick={() => openImagePreview(shownImages, index)}
                  style={{
                    padding: 0,
                    border: "none",
                    background: "#1c1f26",
                    cursor: "pointer",
                    borderRadius: "8px",
                    overflow: "hidden",
                    width: "100%"
                  }}
                >
                  <img
                    src={url}
                    alt="Gym gallery"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    style={{
                      width: "100%",
                      height: "160px",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                </button>
              ))}
            </div>
            {shownImages.length > 3 && (
              <button
                type="button"
                onClick={() => setGalleryAllOpen(true)}
                style={{
                  marginTop: 10,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #187718",
                  background: "transparent",
                  color: "#4ade80",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Show all images ({shownImages.length})
              </button>
            )}
          </>
        ) : (
          <div style={valueStyle}>Not available</div>
        )}
      </ProfileCard>

      <ProfileCard
        title="Co-ordinates"
        onClick={() => copyCard(coordinateText, "Co-ordinates")}
        copiedLabel={copiedCard === "Co-ordinates" ? "Co-ordinates Copied!" : ""}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ background: "#23262e", borderRadius: 8, padding: "10px 12px" }}>
            <div style={labelStyle}>Latitude</div>
            <div style={{ ...valueStyle, fontSize: "18px" }}>{shownLatitude || "Not available"}</div>
          </div>
          <div style={{ background: "#23262e", borderRadius: 8, padding: "10px 12px" }}>
            <div style={labelStyle}>Longitude</div>
            <div style={{ ...valueStyle, fontSize: "18px" }}>{shownLongitude || "Not available"}</div>
          </div>
        </div>
      </ProfileCard>

      <ProfileCard
        title="Passport Rating"
        onClick={() => copyCard(String(shownRating || "Not available"), "Passport Rating")}
        copiedLabel={copiedCard === "Passport Rating" ? "Passport Rating Copied!" : ""}
      >
        <PassportRatingValue rating={shownRating} />
      </ProfileCard>

      <ProfileCard
        title="Timing"
        onClick={() => copyCard(timingText, "Timing")}
        copiedLabel={copiedCard === "Timing" ? "Timing Copied!" : ""}
      >
        {shownSchedule.length ? (
          <div style={{ display: "grid", gap: 6, maxHeight: 220, overflowY: "auto" }}>
            {shownSchedule.map((item) => (
              <div
                key={`${item.day}-${item.start}-${item.end}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid #343844"
                }}
              >
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>{item.day}</span>
                <span style={{ color: item.enabled === false ? "#f87171" : "#9ca3af", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {formatDayTiming(item)}
                </span>
              </div>
            ))}
          </div>
        ) : addedObjects?.cleared ? (
          <div style={valueStyle}>Not available</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ background: "#23262e", borderRadius: 8, padding: "10px 12px" }}>
              <div style={labelStyle}>Start Time</div>
              <div style={valueStyle}>{profile.startTime || "Not available"}</div>
            </div>
            <div style={{ background: "#23262e", borderRadius: 8, padding: "10px 12px" }}>
              <div style={labelStyle}>End Time</div>
              <div style={valueStyle}>{profile.endTime || "Not available"}</div>
            </div>
          </div>
        )}
      </ProfileCard>
    </div>

      {addPopupOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="branch-objects-add-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            padding: 16
          }}
          onClick={() => {
            setImagePreview(null);
            setAddPopupOpen(false);
          }}
        >
          <div
            style={{
              background: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 12,
              padding: "24px 28px",
              maxWidth: 760,
              width: "100%",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 20px 50px rgba(0,0,0,0.45)"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="branch-objects-add-title" style={{ margin: "0 0 16px", fontSize: "1.1rem", fontWeight: 600, color: "#f9fafb" }}>
              {editorMode === "update" ? "Update" : "Add"}
            </h3>
            <div>
              {BRANCH_OBJECT_HEADINGS.map((heading) => (
                <div key={heading} style={{ padding: "12px 0", borderBottom: "1px solid #374151" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        color: "#fff",
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedHeadings.includes(heading)}
                        onChange={() => toggleHeading(heading)}
                        style={{ width: 18, height: 18, accentColor: "#187718", cursor: "pointer" }}
                      />
                      {heading}
                    </label>

                    {selectedHeadings.includes(heading) && heading === "Passport Rating" && (
                      <input
                        type="number"
                        value={passportRatingInput}
                        onChange={(event) => {
                          setPassportRatingInput(event.target.value);
                          setFieldErrors((current) => ({ ...current, rating: "" }));
                        }}
                        placeholder="Enter number"
                        aria-label="Passport rating"
                        min="0"
                        step="any"
                        style={{
                          ...popupInputStyle,
                          width: 160,
                          flexShrink: 0,
                          border: fieldErrors.rating ? "1px solid #f87171" : popupInputStyle.border
                        }}
                      />
                    )}

                    {selectedHeadings.includes(heading) && heading === "Gym Gallery" && (
                      <label
                        style={{
                          position: "relative",
                          overflow: "hidden",
                          display: "inline-flex",
                          alignItems: "center",
                          flexShrink: 0,
                          padding: "10px 16px",
                          borderRadius: 8,
                          border: "2px solid #187718",
                          background: "#187718",
                          color: "#fff",
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Upload Images
                        <input
                          type="file"
                          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                          multiple
                          onChange={handleGalleryUpload}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                            fontSize: 0
                          }}
                        />
                      </label>
                    )}
                  </div>

                  {selectedHeadings.includes(heading) && heading === "Passport Rating" && fieldErrors.rating && (
                    <div style={{ marginTop: 8, color: "#f87171", fontSize: 12, fontWeight: 600 }}>
                      {fieldErrors.rating}
                    </div>
                  )}

                  {selectedHeadings.includes(heading) && heading === "Gym Gallery" && (
                    <div style={{ marginTop: 12, paddingLeft: 30 }}>
                      {(editorMode === "update" ? savedView.images.length : 0) + galleryFiles.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {editorMode === "update" && savedView.images.map((url, index) => (
                            <button
                              key={`saved-${url}-${index}`}
                              type="button"
                              onClick={() => openImagePreview(savedView.images, index)}
                              style={{
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer"
                              }}
                            >
                              <img
                                src={url}
                                alt="Gym gallery"
                                style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, display: "block" }}
                              />
                            </button>
                          ))}
                          {galleryFiles.map((file, index) => (
                            <button
                              key={file.id}
                              type="button"
                              onClick={() => openImagePreview(
                                [...(editorMode === "update" ? savedView.images : []), ...galleryFiles.map((item) => item.url)],
                                (editorMode === "update" ? savedView.images.length : 0) + index
                              )}
                              style={{
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer"
                              }}
                            >
                              <img
                                src={file.url}
                                alt={file.name}
                                style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, display: "block" }}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                      <div style={{ marginTop: (editorMode === "update" ? savedView.images.length : 0) + galleryFiles.length > 0 ? 10 : 0, color: "#d1d5db", fontSize: 14, fontWeight: 600 }}>
                        {(editorMode === "update" ? savedView.images.length : 0) + galleryFiles.length} {((editorMode === "update" ? savedView.images.length : 0) + galleryFiles.length) === 1 ? "image" : "images"} uploaded
                      </div>
                    </div>
                  )}

                  {selectedHeadings.includes(heading) && heading === "Co-ordinates" && (
                    <div style={{ marginTop: 12, paddingLeft: 30, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ color: "#9ca3af", fontSize: 13 }}>
                        Latitude
                        <input
                          type="text"
                          inputMode="decimal"
                          value={latitudeInput}
                          onChange={(event) => {
                            setLatitudeInput(event.target.value);
                            setFieldErrors((current) => ({ ...current, latitude: "" }));
                          }}
                          placeholder="-90 to 90"
                          style={{
                            ...popupInputStyle,
                            marginTop: 6,
                            border: fieldErrors.latitude ? "1px solid #f87171" : popupInputStyle.border
                          }}
                        />
                        {fieldErrors.latitude && (
                          <div style={{ marginTop: 6, color: "#f87171", fontSize: 12, fontWeight: 600 }}>
                            {fieldErrors.latitude}
                          </div>
                        )}
                      </label>
                      <label style={{ color: "#9ca3af", fontSize: 13 }}>
                        Longitude
                        <input
                          type="text"
                          inputMode="decimal"
                          value={longitudeInput}
                          onChange={(event) => {
                            setLongitudeInput(event.target.value);
                            setFieldErrors((current) => ({ ...current, longitude: "" }));
                          }}
                          placeholder="-180 to 180"
                          style={{
                            ...popupInputStyle,
                            marginTop: 6,
                            border: fieldErrors.longitude ? "1px solid #f87171" : popupInputStyle.border
                          }}
                        />
                        {fieldErrors.longitude && (
                          <div style={{ marginTop: 6, color: "#f87171", fontSize: 12, fontWeight: 600 }}>
                            {fieldErrors.longitude}
                          </div>
                        )}
                      </label>
                    </div>
                  )}

                  {selectedHeadings.includes(heading) && heading === "Timing" && (
                    <div className="weekday-time-scroll" style={{ marginTop: 12, display: "grid", gap: 8 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "28px minmax(90px, 1.1fr) 72px minmax(110px, 1fr) minmax(110px, 1fr)",
                          gap: 8,
                          alignItems: "center",
                          color: "#9ca3af",
                          fontSize: 12,
                          fontWeight: 700
                        }}
                      >
                        <span />
                        <span>Days</span>
                        <span>On/Off</span>
                        <span>From</span>
                        <span>To</span>
                      </div>
                      {WEEKDAYS.map((day) => {
                        const entry = weekdayTimes[day];
                        const timesDisabled = !entry.selected || !entry.enabled;
                        return (
                          <div
                            key={day}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "28px minmax(90px, 1.1fr) 72px minmax(110px, 1fr) minmax(110px, 1fr)",
                              gap: 8,
                              alignItems: "center"
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={entry.selected}
                              aria-label={`Select ${day}`}
                              onChange={() => updateWeekdayTime(day, "selected", !entry.selected)}
                              style={{ width: 18, height: 18, accentColor: "#187718", cursor: "pointer" }}
                            />
                            <span style={{ color: "#fff", fontWeight: 600 }}>{day}</span>
                            <button
                              type="button"
                              aria-pressed={entry.enabled}
                              disabled={!entry.selected}
                              onClick={() => updateWeekdayTime(day, "enabled", !entry.enabled)}
                              style={{
                                height: 34,
                                borderRadius: 17,
                                border: "none",
                                background: !entry.selected ? "#374151" : entry.enabled ? "#187718" : "#4b5563",
                                color: "#fff",
                                fontWeight: 700,
                                cursor: entry.selected ? "pointer" : "not-allowed",
                                opacity: entry.selected ? 1 : 0.6
                              }}
                            >
                              {entry.enabled ? "On" : "Off"}
                            </button>
                            <input
                              type="time"
                              aria-label={`${day} from`}
                              value={entry.from}
                              disabled={timesDisabled}
                              onChange={(event) => updateWeekdayTime(day, "from", event.target.value)}
                              style={{ ...popupInputStyle, opacity: timesDisabled ? 0.6 : 1 }}
                            />
                            <input
                              type="time"
                              aria-label={`${day} to`}
                              value={entry.to}
                              disabled={timesDisabled}
                              onChange={(event) => updateWeekdayTime(day, "to", event.target.value)}
                              style={{ ...popupInputStyle, opacity: timesDisabled ? 0.6 : 1 }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {selectedHeadings.length > 0 && (
              <button
                type="button"
                onClick={submitAdd}
                style={{
                  marginTop: 20,
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: "2px solid #187718",
                  backgroundColor: "#187718",
                  color: "#fff",
                  cursor: "pointer",
                  width: "100%",
                  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
                }}
              >
                {editorMode === "update" ? "Update" : "Add"}
              </button>
            )}
          </div>
        </div>
      )}

      {confirmAddOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="branch-objects-confirm-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            padding: 16
          }}
          onClick={() => setConfirmAddOpen(false)}
        >
          <div
            style={{
              background: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 12,
              padding: "24px 28px",
              maxWidth: 400,
              width: "100%",
              boxShadow: "0 20px 50px rgba(0,0,0,0.45)"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="branch-objects-confirm-title" style={{ margin: "0 0 12px", fontSize: "1.1rem", fontWeight: 600, color: "#f9fafb" }}>
              Confirmation
            </h3>
            <p style={{ margin: "0 0 20px", color: "#d1d5db", lineHeight: 1.5, fontSize: "0.95rem" }}>
              Are you sure you want to {editorMode === "update" ? "update" : "add"} {selectedHeadings.join(", ")}?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setConfirmAddOpen(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid #4b5563",
                  background: "#374151",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  width: "100%"
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={confirmAdd}
                disabled={isSaving}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: isSaving ? "#374151" : "#187718",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: isSaving ? "wait" : "pointer",
                  width: "100%"
                }}
              >
                {isSaving ? "Saving..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="branch-objects-delete-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            padding: 16
          }}
          onClick={() => {
            if (!isSaving) setConfirmDeleteOpen(false);
          }}
        >
          <div
            style={{
              background: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 12,
              padding: "24px 28px",
              maxWidth: 400,
              width: "100%",
              boxShadow: "0 20px 50px rgba(0,0,0,0.45)"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="branch-objects-delete-title" style={{ margin: "0 0 12px", fontSize: "1.1rem", fontWeight: 600, color: "#f9fafb" }}>
              Confirmation
            </h3>
            <p style={{ margin: "0 0 20px", color: "#d1d5db", lineHeight: 1.5, fontSize: "0.95rem" }}>
              Are you sure you want to delete branch objects?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(false)}
                disabled={isSaving}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid #4b5563",
                  background: "#374151",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  width: "100%"
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isSaving}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: isSaving ? "#374151" : "#b91c1c",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: isSaving ? "wait" : "pointer",
                  width: "100%"
                }}
              >
                {isSaving ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {galleryAllOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="All gym gallery images"
          onClick={() => setGalleryAllOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.72)",
            padding: 24
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 12,
              padding: 20,
              width: "min(920px, 100%)",
              maxHeight: "85vh",
              overflow: "auto",
              boxShadow: "0 20px 50px rgba(0,0,0,0.45)"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "#f9fafb" }}>
                All images ({shownImages.length})
              </h3>
              <button
                type="button"
                aria-label="Close all images"
                onClick={() => setGalleryAllOpen(false)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "#187718",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10
              }}
            >
              {shownImages.map((url, index) => (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  onClick={() => openImagePreview(shownImages, index)}
                  style={{
                    padding: 0,
                    border: "none",
                    background: "#111827",
                    cursor: "pointer",
                    borderRadius: 8,
                    overflow: "hidden",
                    width: "100%"
                  }}
                >
                  <img
                    src={url}
                    alt={`Gym gallery ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "180px",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {imagePreview && imagePreview.urls[imagePreview.index] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={closeImagePreview}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.88)",
            padding: "24px 96px"
          }}
        >
          <button
            type="button"
            aria-label="Close image"
            onClick={(event) => {
              event.stopPropagation();
              closeImagePreview();
            }}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              width: 44,
              height: 44,
              borderRadius: "50%",
              border: "none",
              background: "#187718",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            aria-label="Previous image"
            disabled={imagePreview.index === 0}
            onClick={(event) => {
              event.stopPropagation();
              showPreviousImage();
            }}
            style={{
              position: "absolute",
              left: 24,
              top: "50%",
              transform: "translateY(-50%)",
              width: 52,
              height: 52,
              borderRadius: "50%",
              border: "none",
              background: imagePreview.index === 0 ? "#374151" : "#187718",
              color: "#fff",
              cursor: imagePreview.index === 0 ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <img
            src={imagePreview.urls[imagePreview.index]}
            alt={`Uploaded image ${imagePreview.index + 1}`}
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: 8
            }}
          />
          <button
            type="button"
            aria-label="Next image"
            disabled={imagePreview.index >= imagePreview.urls.length - 1}
            onClick={(event) => {
              event.stopPropagation();
              showNextImage();
            }}
            style={{
              position: "absolute",
              right: 24,
              top: "50%",
              transform: "translateY(-50%)",
              width: 52,
              height: 52,
              borderRadius: "50%",
              border: "none",
              background: imagePreview.index >= imagePreview.urls.length - 1 ? "#374151" : "#187718",
              color: "#fff",
              cursor: imagePreview.index >= imagePreview.urls.length - 1 ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
